import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import styles from "./ThreeIcon.module.scss";

export type IconType =
  | "cube"
  | "sphere"
  | "diamond"
  | "dodecahedron"
  | "torus"
  | "shield"
  | "full-stack"
  | "ethereum";

interface ThreeIconProps {
  type: IconType;
  color?: number;
}

export const ThreeIcon: React.FC<ThreeIconProps> = ({
  type,
  color = 0xf8bd24,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight || 200;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);

    // Clear container to prevent duplicates (React StrictMode)
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(renderer.domElement);
    }

    // Group to hold both mesh and lines
    const group = new THREE.Group();
    scene.add(group);

    // Materials
    const solidMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      colorWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    const lineMaterial = new THREE.LineBasicMaterial({ color: color });

    const disposables: { dispose: () => void }[] = [
      solidMaterial,
      lineMaterial,
    ];

    // Helper to add standard shape
    const addStandardShape = (geo: THREE.BufferGeometry) => {
      disposables.push(geo);

      const solidMesh = new THREE.Mesh(geo, solidMaterial);
      solidMesh.renderOrder = 1;
      group.add(solidMesh);

      const edges = new THREE.EdgesGeometry(geo);
      disposables.push(edges);

      const lines = new THREE.LineSegments(edges, lineMaterial);
      lines.renderOrder = 2;
      group.add(lines);
    };

    switch (type) {
      case "cube":
        addStandardShape(new THREE.BoxGeometry(2, 2, 2));
        break;
      case "full-stack":
        // 3 Stacked Plates for Full Stack Development
        const plateHeight = 0.4;
        const gap = 0.2;
        const plateGeo = new THREE.BoxGeometry(2, plateHeight, 2);
        disposables.push(plateGeo);

        [-1, 0, 1].forEach((offset) => {
          const y = offset * (plateHeight + gap);

          const plateMesh = new THREE.Mesh(plateGeo, solidMaterial);
          plateMesh.position.y = y;
          plateMesh.renderOrder = 1;
          group.add(plateMesh);

          const plateEdges = new THREE.EdgesGeometry(plateGeo);
          disposables.push(plateEdges);

          const plateLines = new THREE.LineSegments(plateEdges, lineMaterial);
          plateLines.position.y = y;
          plateLines.renderOrder = 2;
          group.add(plateLines);
        });
        break;
      case "ethereum":
        // Top Pyramid
        const topGeo = new THREE.ConeGeometry(1.2, 1.8, 4);
        topGeo.translate(0, 0.9 + 0.1, 0); // Shift up + gap
        addStandardShape(topGeo);

        // Bottom Pyramid
        const bottomGeo = new THREE.ConeGeometry(1.2, 1.5, 4);
        bottomGeo.rotateZ(Math.PI); // Flip upside down
        bottomGeo.translate(0, -0.75 - 0.1, 0); // Shift down + gap
        addStandardShape(bottomGeo);
        break;
      case "sphere":
        addStandardShape(new THREE.IcosahedronGeometry(1.5, 1));
        break;
      case "diamond":
        addStandardShape(new THREE.OctahedronGeometry(1.5, 0));
        break;
      case "dodecahedron":
        addStandardShape(new THREE.DodecahedronGeometry(1.5, 0));
        break;
      case "torus":
        addStandardShape(new THREE.TorusKnotGeometry(1, 0.3, 100, 16));
        break;
      case "shield":
        const shieldShape = new THREE.Shape();
        shieldShape.moveTo(-1.2, 1.2);
        shieldShape.lineTo(1.2, 1.2);
        shieldShape.lineTo(1.2, 0.2);
        shieldShape.quadraticCurveTo(1.2, -1.5, 0, -2.0);
        shieldShape.quadraticCurveTo(-1.2, -1.5, -1.2, 0.2);
        shieldShape.lineTo(-1.2, 1.2);
        const shieldGeo = new THREE.ExtrudeGeometry(shieldShape, {
          depth: 0.4,
          bevelEnabled: true,
          bevelThickness: 0.1,
          bevelSize: 0.1,
          bevelSegments: 2,
        });
        shieldGeo.center();
        addStandardShape(shieldGeo);
        break;
      default:
        addStandardShape(new THREE.BoxGeometry(1, 1, 1));
    }

    // Animation
    let animationId: number;
    let currentSpeed = 0.01;

    const rotationMode = ["shield", "ethereum"].includes(type)
      ? "yaxis"
      : "default";

    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Smoothly interpolate speed based on hover state
      const targetSpeed = isHoveredRef.current ? 0.03 : 0.01;
      currentSpeed += (targetSpeed - currentSpeed) * 0.1;

      if (rotationMode === "yaxis") {
        group.rotation.y += currentSpeed;
      } else {
        group.rotation.x += currentSpeed;
        group.rotation.y += currentSpeed * 2;
      }

      renderer.render(scene, camera);
    };

    animate();

    // Handle Resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight || 200;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
    };
  }, [type, color]);

  return (
    <div
      className={styles["three-icon-container"]}
      ref={containerRef}
      onMouseEnter={() => (isHoveredRef.current = true)}
      onMouseLeave={() => (isHoveredRef.current = false)}
    />
  );
};
